import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';

import { MAX_HISTORY_ENTRIES } from './constant';
import { type NavigationState, type NavigationHistoryContextValue } from './types';

export interface NavigationHistoryProviderProps {
  children?: React.ReactNode;
}

const NavigationHistoryContext = createContext<NavigationHistoryContextValue | undefined>(
  undefined,
);

export function NavigationHistoryProvider({ children }: NavigationHistoryProviderProps) {
  const location = useLocation();
  const navType = useNavigationType();
  // 1. Keep history and index in a single state object
  const [state, setState] = useState<NavigationState>({
    history: [],
    currentIndex: -1,
  });

  useEffect(() => {
    const currentEntry = { path: location.pathname, key: location.key };

    setState((prevState) => {
      let newStack = [...prevState.history];
      let newIndex = prevState.currentIndex;

      if (navType === 'PUSH') {
        // Clear "future" if we were back in history and pushed a new link
        newStack = newStack.slice(0, prevState.currentIndex + 1);
        newStack.push(currentEntry);
        if (newStack.length > MAX_HISTORY_ENTRIES) {
          newStack.shift();
        }
        newIndex = newStack.length - 1;
      } else if (navType === 'REPLACE') {
        // Replace the current entry
        // If the stack is empty (first load), treat REPLACE as a PUSH
        if (newIndex === -1) {
          newStack = [currentEntry];
          newIndex = 0;
        } else {
          newStack[newIndex] = currentEntry;
        }
      } else if (navType === 'POP') {
        // Handle back/forward navigation
        const foundIndex = newStack.findIndex((e) => e.key === location.key);
        if (foundIndex !== -1) {
          newIndex = foundIndex;
        } else {
          // Fallback if key isn't found (e.g. refresh)
          newStack = [currentEntry];
          newIndex = 0;
        }
      }

      return {
        history: newStack,
        currentIndex: newIndex,
      };
    });
  }, [location, navType]);
  // 2. Derive the previous/next paths during render
  const value: NavigationHistoryContextValue = useMemo(
    () => ({
      history: state.history,
      currentIndex: state.currentIndex,
      previousPath: state.history[state.currentIndex - 1]?.path || null,
      nextPath: state.history[state.currentIndex + 1]?.path || null,
    }),
    [state],
  );

  return (
    <NavigationHistoryContext.Provider value={value}>{children}</NavigationHistoryContext.Provider>
  );
}

export function useNavigationHistory(): NavigationHistoryContextValue {
  const context = useContext(NavigationHistoryContext);
  if (context === undefined) {
    throw new Error('useNavigationHistory must be used within a NavigationHistoryProvider');
  }
  return context;
}
